import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError } from "axios";

import { RootState } from "features/config/store";
import { DailyProduct } from "features/ms6/core/domain/DailyProductData.model";
import {
  DailyProductRepository,
  DailyProductResponse,
} from "features/ms6/data/ms6.repository";

// Enum for the state of the fetch operation
export enum DailyProductState {
  initial,
  inProgress,
  success,
  fail,
}

// Initial state for the slice
interface InitialState {
  status: DailyProductState;
  message: string;
  data: DailyProduct | undefined;
}

const initialState: InitialState = {
  status: DailyProductState.initial,
  message: "",
  data: undefined,
};

// Async thunk to fetch daily product data
export const dailyProduct = createAsyncThunk(
  "dailyProduct",
  async (_, { rejectWithValue }) => {
    try {
      const response: DailyProductResponse = await DailyProductRepository();
      return response;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (!error.response) {
          throw error;
        }
        throw rejectWithValue(error.response.data.message);
      }
      throw error;
    }
  }
);

// Main slice
export const dailyProductSlice = createSlice({
  name: "dailyProduct",
  initialState,
  reducers: {
    resetDailyProduct: (state) => {
      state.status = DailyProductState.initial;
      state.message = "";
      state.data = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(dailyProduct.pending, (state) => {
        state.status = DailyProductState.inProgress;
      })
      .addCase(dailyProduct.fulfilled, (state, action) => {
        if (action.payload) {
          const { data, message } = action.payload.data; // Destructure from action.payload.data
          state.status = DailyProductState.success;
          state.message = message;
          state.data = data;
        }
      })
      .addCase(dailyProduct.rejected, (state, action) => {
        state.status = DailyProductState.fail;
        state.message = action.payload as string;
      });
  },
});

// Selector to access the daily product state
export const selectDailyProduct = (state: RootState) => state.dailyProduct;

// Export actions and reducer
export const { resetDailyProduct } = dailyProductSlice.actions;
export default dailyProductSlice.reducer;
